import React, { useState } from 'react';
import { User, Database, Brain, CreditCard, Heart, X, CheckCircle2, XCircle, Loader2, Info } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';

interface ProfileIconProps {
  onProfileSettings: () => void;
  onUpgradePremium?: () => void;
  onViewLearning?: () => void;
  dbStatus?: 'connected' | 'disconnected' | 'connecting';
}

const ProfileIcon: React.FC<ProfileIconProps> = ({
  onProfileSettings,
  onUpgradePremium,
  onViewLearning,
  dbStatus = 'disconnected',
}) => {
  const { user } = useAuth();
  const userProfile = user?.user_metadata;
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [experiences] = useState(() => {
    const buffer = JSON.parse(localStorage.getItem('rlReplayBuffer') || '[]');
    return buffer.length;
  });
  const [showDescription, setShowDescription] = useState(false);

  const getStatusIcon = () => {
    switch (dbStatus) {
      case 'connected':
        return <CheckCircle2 className="h-4 w-4 text-green-400" />;
      case 'connecting':
        return <Loader2 className="h-4 w-4 text-yellow-400 animate-spin" />;
      default:
        return <XCircle className="h-4 w-4 text-red-400" />;
    }
  };

  const getStatusText = () => {
    switch (dbStatus) {
      case 'connected':
        return 'Connected';
      case 'connecting':
        return 'Connecting...';
      default:
        return 'Disconnected';
    }
  };

  return (
    <div className="relative">
      {/* Description Modal */}
      {showDescription && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-[9999] flex items-center justify-center p-4">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-lg p-6 space-y-6">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <Heart className="h-8 w-8 text-teal-600" />
                <h2 className="text-2xl font-semibold text-gray-900">Thank You, Caregivers!</h2>
              </div>
              <button
                onClick={() => setShowDescription(false)}
                className="p-2 hover:bg-gray-100 rounded-full transition-colors"
              >
                <X className="h-6 w-6 text-gray-500" />
              </button>
            </div>
            
            <div className="space-y-4 text-gray-600">
              <p>
                Angel AI was created in loving memory of my mother Angel, whose experience with illness inspired us to improve healthcare technology. Our mission is to support dedicated caregivers like you who make a difference in people's lives every day.
              </p>
              
              <p>
                We're committed to making your work easier by providing intuitive tools that help you:
              </p>
              
              <ul className="space-y-2 list-disc pl-4">
                <li>Document care details efficiently</li>
                <li>Track client progress seamlessly</li>
                <li>Generate professional summaries automatically</li>
                <li>Focus more time on what matters most - your clients</li>
              </ul>
              
              <p>
                Your dedication to providing exceptional care inspires us to continuously improve our technology. Together, we can create better outcomes for those in need of care.
              </p>
            </div>
          </div>
        </div>
      )}

      <button
        onClick={() => setIsDropdownOpen(!isDropdownOpen)}
        className="p-2 text-white/90 hover:text-white hover:bg-white/10 rounded-lg transition-all"
      >
        {userProfile?.avatar_url ? (
          <img
            src={userProfile.avatar_url}
            alt="Profile"
            className="h-8 w-8 rounded-full object-cover border-2 border-white/20"
          />
        ) : (
          <User className="h-6 w-6 text-white/90" />
        )}
      </button>
      
      {isDropdownOpen && (
        <>
          <div 
            className="fixed inset-0 z-40"
            onClick={() => setIsDropdownOpen(false)}
          />
          <div className="absolute right-0 mt-2 w-56 bg-white rounded-lg shadow-lg overflow-hidden z-50">
            {/* Database Status */}
            <div className="px-4 py-3 border-b border-gray-100">
              <div className="flex items-center gap-2 text-sm">
                {getStatusIcon()}
                <span className="text-gray-700">{getStatusText()}</span>
              </div>
            </div>
            
            {/* Learning Progress */}
            <button
              onClick={() => {
                setShowDescription(true);
                setIsDropdownOpen(false);
              }}
              className="w-full px-4 py-2 text-left hover:bg-gray-50 flex items-center gap-2"
            >
              <Heart className="h-5 w-5 text-teal-600" />
              <span className="text-gray-700">Our Mission</span>
            </button>

            {onViewLearning && (
              <button
                onClick={() => {
                  onViewLearning();
                  setIsDropdownOpen(false);
                }}
                className="w-full px-4 py-2 text-left hover:bg-gray-50 flex items-center gap-2"
              >
                <Brain className="h-5 w-5 text-gray-500" />
                <span className="text-gray-700">Learning Progress</span>
                {experiences > 0 && (
                  <span className="ml-auto bg-purple-100 text-purple-600 text-xs font-medium px-2 py-0.5 rounded-full">
                    {experiences}
                  </span>
                )}
              </button>
            )}
            
            {/* Premium Upgrade */}
            {onUpgradePremium && (
              <button
                onClick={() => {
                  onUpgradePremium();
                  setIsDropdownOpen(false);
                }}
                className="w-full px-4 py-2 text-left hover:bg-gray-50 flex items-center gap-2"
              >
                <CreditCard className="h-5 w-5 text-gray-500" />
                <span className="text-gray-700">Upgrade to Premium</span>
              </button>
            )}
            
            {/* Profile Settings */}
            <button
              onClick={() => {
                onProfileSettings();
                setIsDropdownOpen(false);
              }}
              className="w-full px-4 py-2 text-left hover:bg-gray-50 flex items-center gap-2"
            >
              <User className="h-5 w-5 text-gray-500" />
              <span className="text-gray-700">Profile Settings</span>
            </button>
          </div>
        </>
      )}
    </div>
  );
};

export default ProfileIcon;